// ---------------------------------------------------------------------------
// Toggle. The whole row is the control, label and description included, because
// the settings that use it carry a line of explanation and a 46px track is a
// poor target next to a 300px label that looks tappable.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';

export function Toggle(props: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}): ReactElement {
  const { checked, onChange, label, description, disabled = false } = props;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`flex min-h-11 w-full items-center gap-3 text-left ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-[14px] font-medium text-ink">{label}</span>
        {description && <span className="text-[12px] text-ink-dim">{description}</span>}
      </span>

      <span
        aria-hidden="true"
        className={`relative ml-auto h-[26px] w-[46px] shrink-0 rounded-full transition-colors duration-150 ease-out ${
          checked ? 'bg-blue-500' : 'bg-raised'
        }`}
      >
        {/* Transform rather than `left`: the knob is the one thing on this
            control that moves, and moving it by layout jitters it on a phone. */}
        <span
          className="absolute top-[3px] left-[3px] h-5 w-5 rounded-full bg-[var(--c-tile-letter)] transition-transform duration-150 ease-out"
          style={{ transform: `translateX(${checked ? 20 : 0}px)` }}
        />
      </span>
    </button>
  );
}
